// React
import React, { useState, useEffect } from 'react';

// Assets
import { AudioOutlined, AudioMutedOutlined } from '@ant-design/icons';

// Packages
import {
    DisconnectButton,
    GridLayout,
    LiveKitRoom,
    ParticipantTile,
    RoomAudioRenderer,
    useTrackToggle,
    useTracks,
    useRoomContext
} from '@livekit/components-react';
import { createLocalAudioTrack, Track } from 'livekit-client';
import { Box, Button, IconButton, Stack, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CallEndRounded from '@mui/icons-material/CallEndRounded';
import MicOffRounded from '@mui/icons-material/MicOffRounded';
import MicRounded from '@mui/icons-material/MicRounded';
import PersonAddAlt1Rounded from '@mui/icons-material/PersonAddAlt1Rounded';

// Components
import CallStageStatus from './CallStageStatus';

// Styles
import '@livekit/components-styles';

const serverUrl = 'wss://sage-9htia2aq.livekit.cloud';

const MIC_TIMEOUT_MS = 10000;

const CONTROL_HEIGHT = 44;

// Kiosk name and signal badges ride the top corners, leaving the bottom edge to the
// call controls. The focus toggle is hidden - there is only ever one tile, and it
// would sit on top of the signal badge.
const tileOverlaySx = {
    display: 'contents',
    '& .lk-participant-metadata': {
        top: '10px',
        right: '10px',
        bottom: 'auto',
        left: '10px',
        alignItems: 'flex-start'
    },
    '& .lk-participant-metadata-item': {
        padding: '4px 8px',
        borderRadius: '999px',
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        backdropFilter: 'blur(8px)',
        fontSize: '0.8rem'
    },
    '& .lk-focus-toggle-button': { display: 'none' }
};

// ==============================|| VIRTUAL GUARD ||============================== //

function MyVideoConference({
    localParticipant,
    propertyAddress,
    setPropertyAddress,
    onLogVisitor,
    compact
}) {
    const [remoteConnected, setRemoteConnected] = useState(null);
    const [disconnecting, setDisconnecting] = useState(false);
    const lkRoom = useRoomContext();
    const theme = useTheme();

    const trackRefs = useTracks(
        [
            { source: Track.Source.Camera, withPlaceholder: true },
            { source: Track.Source.Microphone }
        ],
        {
            onlySubscribed: false
        }
    );

    const localParticipantAudioTrack = trackRefs.find(
        trackRef =>
            trackRef.source === 'microphone' &&
            trackRef?.participant.identity === localParticipant
    );

    const remoteCamTrackRef = trackRefs.filter(
        trackRef =>
            trackRef?.participant.identity !== localParticipant &&
            trackRef?.participant.identity !== '' &&
            trackRef.source === 'camera'
    );

    useEffect(() => {
        if (!remoteConnected) {
            if (remoteCamTrackRef && remoteCamTrackRef?.length) {
                setRemoteConnected(true);
            }
        }
        if (remoteConnected && !remoteCamTrackRef.length) {
            setDisconnecting(true);
        }
    }, [remoteConnected, setRemoteConnected, remoteCamTrackRef]);

    useEffect(() => {
        if (disconnecting && lkRoom && lkRoom?.state === 'connected') {
            lkRoom.disconnect();
        }
    }, [disconnecting, lkRoom]);

    const { buttonProps, enabled } = useTrackToggle(localParticipantAudioTrack || []);

    const handleLogVisitor = () => {
        setPropertyAddress(propertyAddress);
        onLogVisitor();
    };

    const compactToolbar = (
        <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            width="max-content"
            sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.55)',
                borderRadius: 10,
                position: 'absolute',
                p: '2px 4px',
                zIndex: 1,
                bottom: '10px',
                right: '10px'
            }}
        >
            <IconButton
                size="small"
                sx={{ color: 'white' }}
                onClick={buttonProps.onClick}
                aria-label={enabled ? 'mute microphone' : 'unmute microphone'}
            >
                {!enabled && <AudioMutedOutlined style={{ fontSize: '0.9rem' }} />}
                {enabled && <AudioOutlined style={{ fontSize: '0.9rem' }} />}
            </IconButton>
        </Stack>
    );

    const pillSx = {
        height: CONTROL_HEIGHT,
        borderRadius: '999px',
        paddingX: 2.5,
        fontWeight: 600,
        textTransform: 'none',
        boxShadow: 'none',
        whiteSpace: 'nowrap'
    };

    const fullToolbar = (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
                position: 'absolute',
                zIndex: 1,
                bottom: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '6px',
                borderRadius: '999px',
                backgroundColor: 'rgba(15, 15, 15, 0.55)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)'
            }}
        >
            <Button
                variant="contained"
                color="primary"
                startIcon={<PersonAddAlt1Rounded />}
                onClick={handleLogVisitor}
                sx={{ ...pillSx, '&:hover': { boxShadow: 'none' } }}
            >
                Log Visitor
            </Button>

            <IconButton
                onClick={buttonProps.onClick}
                aria-label={enabled ? 'mute microphone' : 'unmute microphone'}
                aria-pressed={!enabled}
                sx={{
                    width: CONTROL_HEIGHT,
                    height: CONTROL_HEIGHT,
                    color: enabled ? 'common.white' : theme.palette.error.main,
                    backgroundColor: enabled
                        ? 'rgba(255, 255, 255, 0.14)'
                        : 'common.white',
                    transition: 'background-color 150ms, color 150ms',
                    '&:hover': {
                        backgroundColor: enabled
                            ? 'rgba(255, 255, 255, 0.24)'
                            : alpha(theme.palette.common.white, 0.88)
                    }
                }}
            >
                {enabled ? <MicRounded /> : <MicOffRounded />}
            </IconButton>

            <Box
                component={DisconnectButton}
                sx={{
                    // Tripled class to outrank LiveKit's own .lk-disconnect-button
                    // rules (red outline, danger hover) regardless of load order.
                    '&&&': {
                        ...pillSx,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        border: 'none',
                        fontFamily: 'inherit',
                        fontSize: '0.875rem',
                        lineHeight: 1,
                        color: theme.palette.common.white,
                        backgroundColor: theme.palette.error.main,
                        transition: 'background-color 150ms'
                    },
                    '&&&:not(:disabled):hover, &&&:not(:disabled):active': {
                        color: theme.palette.common.white,
                        backgroundColor: theme.palette.error.dark
                    }
                }}
            >
                <CallEndRounded fontSize="small" />
                End Call
            </Box>
        </Stack>
    );

    return (
        <>
            {compact ? compactToolbar : fullToolbar}
            <Box sx={tileOverlaySx}>
                <GridLayout tracks={remoteCamTrackRef}>
                    <ParticipantTile />
                </GridLayout>
            </Box>
        </>
    );
}

const VideoCall = ({
    token,
    localParticipant,
    endPanel,
    propertyAddress,
    setPropertyAddress,
    onLogVisitor,
    compact
}) => {
    const [audioTrack, setAudioTrack] = useState(null);
    const [audioError, setAudioError] = useState(null);
    const [audioAttempt, setAudioAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        let track;

        // getUserMedia can hang (mic held by another tab or app, device asleep) as well as
        // reject. Either way the operator must not be left on 'Loading...' with no way out.
        const timeout = setTimeout(() => {
            if (!cancelled && !track) setAudioError('Microphone is not responding.');
        }, MIC_TIMEOUT_MS);

        const configureAudioTrack = async () => {
            try {
                track = await createLocalAudioTrack({
                    noiseSuppression: true,
                    echoCancellation: true,
                    autoGainControl: true
                });
            } catch (error) {
                if (!cancelled) setAudioError(error?.message || 'Microphone unavailable.');
                return;
            }

            if (cancelled) {
                // Unmounted while getUserMedia was still pending - release the
                // mic immediately instead of leaving it open with nothing holding
                // a reference to it.
                track.stop();
                return;
            }

            clearTimeout(timeout);
            setAudioError(null);
            setAudioTrack(track);
        };

        configureAudioTrack();

        return () => {
            cancelled = true;
            clearTimeout(timeout);
            // This track is only ever used to gate the loading state above - it's
            // never attached to the LiveKitRoom, so nothing else stops it. Without
            // this, the mic stays active (OS/browser indicator included) after the
            // call ends and this component unmounts.
            track?.stop();
        };
    }, [audioAttempt]);

    if (!audioTrack) {
        return (
            <CallStageStatus
                message={audioError || 'Connecting audio...'}
                error={!!audioError}
                onRetry={() => {
                    setAudioError(null);
                    setAudioAttempt(attempt => attempt + 1);
                }}
                onEnd={() => endPanel()}
            />
        );
    }

    return (
        <LiveKitRoom
            video={false}
            audio={true}
            token={token}
            serverUrl={serverUrl}
            data-lk-theme="default"
            onDisconnected={() => endPanel()}
            options={{
                adaptiveStream: false,
                publishDefaults: {
                    audioPreset: { maxBitrate: 128000 },
                    dtx: false
                }
            }}
        >
            <MyVideoConference
                localParticipant={localParticipant}
                propertyAddress={propertyAddress}
                setPropertyAddress={setPropertyAddress}
                onLogVisitor={onLogVisitor}
                compact={compact}
            />
            <RoomAudioRenderer />
        </LiveKitRoom>
    );
};

export default VideoCall;
